import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePusher } from './PusherContext';
import { AgentEvents } from '@dreamai/types';
import api from '../api';

interface Confirmation {
  id: string;
  task: string;
  method_name: string;
  method_input: Record<string, any>;
  status: 'PENDING' | 'CONFIRMED' | 'CANCELLED' | 'ERROR';
  created_at: string;
  thread_id: string;
  notification_text?: string;
}

interface ConfirmationsContextValue {
  confirmations: Confirmation[];
  confirmationCount: number;
  hasConfirmations: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

const ConfirmationsContext = createContext<
  ConfirmationsContextValue | undefined
>(undefined);

interface ConfirmationsProviderProps {
  threadId: string | null;
  children: ReactNode;
}

export const ConfirmationsProvider: React.FC<ConfirmationsProviderProps> = ({
  threadId,
  children,
}) => {
  const [confirmations, setConfirmations] = useState<Confirmation[]>([]);
  const [loading, setLoading] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const pusherContext = usePusher(true);

  const fetchConfirmations = useCallback(async () => {
    if (!threadId) {
      setConfirmations([]);
      return;
    }

    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const response = await api.get(`/confirmation-auth/thread/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setConfirmations(response.data.confirmations || []);
      }
    } catch (error) {
      console.error('Error fetching confirmations:', error);
    } finally {
      setLoading(false);
    }
  }, [threadId, getAccessTokenSilently]);

  // Initial fetch when threadId changes
  useEffect(() => {
    fetchConfirmations();
  }, [fetchConfirmations]);

  // Listen for Pusher events
  useEffect(() => {
    if (!pusherContext?.pusherInstance || !threadId) {
      return;
    }

    const channelName = `${process.env.REACT_APP_PUSHER_ENV}-private-thread-${threadId}`;
    const channel = pusherContext.pusherInstance.subscribe(channelName);

    const handleConfirmationCreated = () => {
      fetchConfirmations();
    };

    const handleConfirmationStatusChanged = () => {
      fetchConfirmations();
    };

    channel.bind(AgentEvents.CONFIRMATION_CREATED, handleConfirmationCreated);
    channel.bind(
      AgentEvents.CONFIRMATION_STATUS_CHANGED,
      handleConfirmationStatusChanged,
    );

    return () => {
      channel.unbind(
        AgentEvents.CONFIRMATION_CREATED,
        handleConfirmationCreated,
      );
      channel.unbind(
        AgentEvents.CONFIRMATION_STATUS_CHANGED,
        handleConfirmationStatusChanged,
      );
    };
  }, [pusherContext, threadId, fetchConfirmations]);

  const value: ConfirmationsContextValue = {
    confirmations,
    confirmationCount: confirmations.length,
    hasConfirmations: confirmations.length > 0,
    loading,
    refetch: fetchConfirmations,
  };

  return (
    <ConfirmationsContext.Provider value={value}>
      {children}
    </ConfirmationsContext.Provider>
  );
};

export const useConfirmations = (): ConfirmationsContextValue => {
  const context = useContext(ConfirmationsContext);
  if (!context) {
    throw new Error(
      'useConfirmations must be used within a ConfirmationsProvider',
    );
  }
  return context;
};

export const useConfirmationsOptional = ():
  | ConfirmationsContextValue
  | undefined => {
  return useContext(ConfirmationsContext);
};
